/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
/* eslint-disable max-classes-per-file */
import React from 'react';
import _ from 'lodash';
import { decorate, computed, observable, runInAction } from 'mobx';
import { observer, inject, Observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import {
  Container,
  Checkbox,
  Loader,
  Dimmer,
  Popup,
  Icon,
  Tab,
} from 'semantic-ui-react';
import TimeAgo from 'react-timeago';

import { gotoFn } from '@amzn/base-ui/dist/helpers/routing';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { isStoreLoading, isStoreError, isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import { displayError, displaySuccess } from '@amzn/base-ui/dist/helpers/notification';

import {
  isAppStreamEnabled,
  enableWorkspaceScheduler,
  schedulerIdleGraceMultiplier,
  schedulerTimezone,
} from '../../helpers/settings';
import { getScSchedulingInfo } from '../../helpers/api';
import { parseScheduleValue, MODE_GROUPS, MODE_CUSTOM } from '../../helpers/schedule-tag-format';
import {
  summariseWindows,
  formatSummaryLine,
  displayGroupName,
  formatDuration,
  projectedStopFrom,
  formatProjectedStopWithZone,
  IDLE_STOP_LOCKED_NOTICE,
} from '../../helpers/schedule-display';
import { formatConfigName, parseEnvTypeName } from '../../helpers/env-type-display';
import By from '../helpers/By';
import DeprecatedBadge from '../helpers/DeprecatedBadge';
import Expiry from '../helpers/Expiry';
import ScEnvironmentButtons from './parts/ScEnvironmentButtons';
import ScEnvironmentConnections from './parts/ScEnvironmentConnections';
import ScEnvironmentCost from './parts/ScEnvironmentCost';
import ScEnvironmentTypeName from './parts/ScEnvironmentTypeName';
import ScEnvironmentCostTable from './parts/ScEnvironmentCostTable';
import ScEnvironmentCpuChart from './parts/ScEnvironmentCpuChart';
import StorageUsageDisplay from './parts/StorageUsageDisplay';
import { getRegionDisplayText } from '../../models/constants/aws-regions';

// Icon style fix for alignment (matches workspace card pattern)
const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// Parse hardware info from config description (matches ScEnvironmentCard pattern)
function parseHardwareInfo(desc) {
  if (!desc) return { cpu: null, memory: null };
  const cpuMatch = desc.match(/(\d+)\s*(?:v?CPU|cpu)/i);
  const threadsMatch = desc.match(/\((\d+)\s*threads?\)/i);
  const memoryMatch = desc.match(/(\d+(?:\.\d+)?)\s*(?:GiB|GB|Gib)\s*(?:Memory|RAM)?/i);
  let cpuDisplay = null;
  if (cpuMatch) {
    cpuDisplay = cpuMatch[1];
    if (threadsMatch) cpuDisplay += ` (${threadsMatch[1]}t)`;
    cpuDisplay += ' vCPU';
  }
  return { cpu: cpuDisplay, memory: memoryMatch ? `${memoryMatch[1]} GiB` : null };
}

// A minutes value from either the SchedulingInfo response or the tag mirror, as a usable number or
// null. Both sources are nullable and the response's value has been through JSON, so neither can be
// trusted to be a number.
function normaliseMinutes(value) {
  if (_.isNil(value)) return null;

  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}

// The Schedule_Tag value as a string, from either source.
function normaliseScheduleValue(value) {
  return _.isNil(value) ? '' : String(value);
}

// Alert component for modern error/warning display
function Alert({ type, title, message, link }) {
  const className = type === 'error' ? 'workspace-alert workspace-alert-error' : 'workspace-alert workspace-alert-warning';
  const iconName = type === 'error' ? 'exclamation circle' : 'warning sign';

  return (
    <div className={className}>
      <Icon name={iconName} style={iconStyle} />
      <div>
        <strong>{title}:</strong> {message}
        {link && (
          <a 
            href={link.href} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ marginLeft: '6px', textDecoration: 'underline' }}
          >
            {link.text}
          </a>
        )}
      </div>
    </div>
  );
}

// This component is used with the TabPane to replace the default Segment wrapper since
// we don't want to display the border.
// eslint-disable-next-line react/prefer-stateless-function
class TabPaneWrapper extends React.Component {
  render() {
    return <>{this.props.children}</>;
  }
}

// expected props
// - scEnvironmentsStore (via injection)
// - userStore (via injection)
class ScEnvironmentDetailPage extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      // A flag to indicate if we are processing the call to trigger the terminate action
      this.processing = false;
      // The SchedulingInfo response, read once on mount. Null until it arrives, and after a failure.
      this.schedulingInfo = null;
      this.schedulingLoading = false;
      this.schedulingFailed = false;
    });
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const store = this.getEnvStore();
    if (store) {
      swallowError(store.load());
      store.startHeartbeat();
    }

    if (!isStoreReady(this.envTypesStore)) {
      swallowError(this.envTypesStore.load());
    }

    if (enableWorkspaceScheduler) {
      this.loadSchedulingInfo();
    }
  }

  /**
   * Reads the workspace's scheduling state once, on mount.
   *
   * This page is one workspace, so one call is affordable, and it is the only way to get the things the
   * tag mirror cannot hold: the hours a preset resolves to, whether the workspace configuration locks
   * the idle timeout, and the server-computed projected stop time. The workspace list makes no such
   * call and renders from the mirror alone.
   *
   * Failure is swallowed on purpose. Scheduling display is advisory (Requirement 11.8): the two cards
   * fall back to the mirror and say quietly that the live details are unavailable, and the rest of the
   * page is unaffected. An error banner or a notification for a card that cannot be filled in would
   * make an incidental failure look like a broken workspace. Not retried and not polled: the two
   * cards describe configuration, which does not move on its own.
   */
  async loadSchedulingInfo() {
    runInAction(() => {
      this.schedulingLoading = true;
      this.schedulingFailed = false;
    });

    try {
      const info = await getScSchedulingInfo(this.instanceId);
      runInAction(() => {
        this.schedulingInfo = _.isPlainObject(info) ? info : null;
        this.schedulingFailed = !_.isPlainObject(info);
        this.schedulingLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.schedulingInfo = null;
        this.schedulingFailed = true;
        this.schedulingLoading = false;
      });
    }
  }

  componentWillUnmount() {
    const store = this.getEnvStore();
    if (store) {
      store.stopHeartbeat();
    }
  }

  get envsStore() {
    return this.props.scEnvironmentsStore;
  }

  get envTypesStore() {
    return this.props.envTypesStore;
  }

  get userStore() {
    return this.props.userStore.user;
  }

  get instanceId() {
    return (this.props.match.params || {}).instanceId;
  }

  getEnvStore() {
    const envsStore = this.envsStore;
    const envId = this.instanceId;
    return envsStore.getScEnvironmentStore(envId);
  }

  getEnvTypeConfigsStore(env = null) {
    env = env || this.getEnv();
    const configsStore = this.envTypesStore.getEnvTypeConfigsStore(env.envTypeId);
    return configsStore;
  }

  getConfiguration(env) {
    const configsStore = this.getEnvTypeConfigsStore(env);
    if (configsStore && !isStoreReady(configsStore)) {
      swallowError(configsStore.load());
    }
    const config = configsStore.getEnvTypeConfig(env.envTypeConfigId);
    return config;
  }

  getEnv() {
    const store = this.getEnvStore();
    if (!store) return {};
    if (!isStoreReady(store)) return {};
    return store.scEnvironment;
  }

  render() {
    const store = this.getEnvStore();
    let content = null;

    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} className="p0" />;
    } else if (isStoreLoading(store)) {
      content = <ProgressPlaceHolder />;
    } else if (isStoreReady(store)) {
      content = this.renderMain();
    } else {
      content = null;
    }

    return (
      <Container className="mt3">
        {this.renderBreadcrumb()}
        {content}
      </Container>
    );
  }

  renderBreadcrumb() {
    const envId = this.instanceId;
    const goto = gotoFn(this);

    return (
      <div className="workspace-detail-breadcrumb">
        <a href="#" onClick={(e) => { e.preventDefault(); goto('/workspaces'); }}>
          Workspaces
        </a>
        <span className="divider">
          <Icon name="angle right" style={iconStyle} />
        </span>
        <span className="section active">Workspace # {envId}</span>
      </div>
    );
  }

  renderMain() {
    const env = this.getEnv();
    const state = env.state;
    const hexColor = state.hexColor || '#6b7280';

    return (
      <>
        {/* Modern Header Card */}
        <div className="workspace-detail-header-card">
          <div className="workspace-card-accent" style={{ background: hexColor }} />
          <div className="workspace-card-content">
            {this.renderHeaderContent(env)}
            {this.renderAlerts(env)}
            {this.renderInlineConnections(env)}
            {this.renderButtons(env)}
          </div>
        </div>

        {/* Info Card Grid */}
        {this.renderInfoCardGrid(env)}

        {this.renderTabs(env)}
      </>
    );
  }

  renderHeaderContent(env) {
    const state = env.state;
    const hexColor = state.hexColor || '#6b7280';
    const hexBg = state.hexBg || '#f3f4f6';
    const { deprecated } = parseEnvTypeName(this.envTypesStore.getEnvType(env.envTypeId)?.name);

    return (
      <>
        {/* Header Row with Title and Status Badge */}
        <div className="workspace-card-header">
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Icon name="computer" size="large" style={{ color: '#6b7280' }} />
              <h2 className="workspace-card-title" style={{ margin: 0 }}>{env.name}</h2>
            </div>
            <div className="workspace-card-description" style={{ marginLeft: '42px' }}>
              {env.description || 'No description for this workspace was provided.'}
            </div>
          </div>
          <div className="workspace-card-header-badges">
            {deprecated && <DeprecatedBadge />}
            <Popup
              trigger={
                <span 
                  className="workspace-status-badge"
                  style={{ background: hexBg, color: hexColor }}
                >
                  <Icon name={state.spinner ? 'spinner' : 'circle'} loading={state.spinner} style={iconStyle} />
                  {state.display}
                </span>
              }
              content={state.tip}
              size="small"
              position="top center"
              inverted
            />
          </div>
        </div>

        {/* Meta Row */}
        <div className="workspace-meta-row" style={{ marginLeft: '42px' }}>
          <span className="workspace-meta-item">
            <Icon name="clock outline" style={iconStyle} />
            Created <TimeAgo date={env.createdAt} />
          </span>
          <span className="workspace-meta-item">
            <Icon name="user outline" style={iconStyle} />
            <By uid={env.createdBy} />
          </span>
          {env.status !== "EXPIRED" && env.status !== "TERMINATED" && (
            <span className="workspace-meta-item">
              <Icon name="hourglass half" style={iconStyle} />
              <Expiry date={env.expiresAt} />
            </span>
          )}
          <span className="workspace-meta-item">
            <Icon name="briefcase" style={iconStyle} />
            {_.isEmpty(env.projectId) ? 'No project' : env.projectId}
          </span>
          {!_.isEmpty(env.displayRegion) && (
            <span className="workspace-meta-item">
              <Icon name="globe" style={iconStyle} />
              {getRegionDisplayText(env.displayRegion)}
            </span>
          )}
          <span className="workspace-meta-item">
            <Icon name="hashtag" style={iconStyle} />
            {env.id}
            {/* Same copy control as the workspace card. */}
            <Icon
              name="copy outline"
              style={{
                ...iconStyle,
                cursor: 'pointer',
                color: '#9ca3af',
                marginLeft: '2px',
                fontSize: '0.85em',
                verticalAlign: 'middle',
              }}
              onClick={e => {
                e.stopPropagation();
                navigator.clipboard
                  .writeText(env.id)
                  .then(() => displaySuccess('Workspace ID copied', 'Copied!'))
                  .catch(() => {});
              }}
              title="Copy workspace ID"
              data-testid="detail-copy-workspace-id"
            />
          </span>
        </div>
      </>
    );
  }

  renderAlerts(env) {
    const alerts = [];

    // Error message
    if (!_.isEmpty(env.error)) {
      alerts.push(
        <Alert key="error" type="error" title="Error" message={env.error} />
      );
    }

    // COMPLETED_EXPIRED status - workspace will auto-stop soon
    if (env.status === 'COMPLETED_EXPIRED') {
      alerts.push(
        <Alert 
          key="completed-expired" 
          type="error" 
          title="Auto-Stop Imminent" 
          message="This workspace has expired and will automatically stop soon. Please save your work immediately."
        />
      );
    }

    // Expiry warning - 14 days or less remaining
    if (env.expiresAt && env.status !== 'EXPIRED' && env.status !== 'TERMINATED' && env.status !== 'COMPLETED_EXPIRED') {
      const expiryDate = new Date(env.expiresAt);
      const now = new Date();
      const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysRemaining <= 14 && daysRemaining > 0) {
        alerts.push(
          <Alert 
            key="expiry-warning" 
            type="warning" 
            title="Expiring Soon" 
            message={`This workspace expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}. Please migrate your data to a new workspace as soon as possible.`}
          />
        );
      }
    }

    // AppStream warning
    if (isAppStreamEnabled && !env.isAppStreamConfigured && env.state.canTerminate) {
      alerts.push(
        <Alert 
          key="appstream" 
          type="warning" 
          title="Non-AppStream Workspace" 
          message="Please terminate this workspace. It is not supported by AppStream."
        />
      );
    }

    // Legacy RStudio warning
    const metaConnection1Type = env.outputs.find(obj => obj.OutputKey === 'MetaConnection1Type');
    if (metaConnection1Type && metaConnection1Type.OutputValue.toLowerCase() === 'rstudio' && env.state.canTerminate) {
      alerts.push(
        <Alert 
          key="rstudio" 
          type="warning" 
          title="Legacy RStudio" 
          message="This environment type is deprecated. Please use RStudioV2 instead."
          link={{
            href: "https://github.com/awslabs/service-workbench-on-aws/blob/mainline/docs/Service_Workbench_Post_Deployment_Guide.pdf",
            text: "Learn more"
          }}
        />
      );
    }

    if (alerts.length === 0) return null;

    return <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>{alerts}</div>;
  }

  renderInlineConnections(env) {
    const state = env.state;
    if (!state.canConnect) return null;
    const config = this.getConfiguration(env);

    return (
      <div style={{ marginTop: '16px' }}>
        <ScEnvironmentConnections scEnvironment={env} config={config} />
      </div>
    );
  }

  renderInfoCardGrid(env) {
    const studyIds = _.get(env, 'studyIds', []);
    const studyCount = _.size(studyIds);
    const isAdmin = this.userStore.isAdmin;
    const canToggleLock = this.envsStore.canChangeState(env.id) && env.state.canTerminate;
    const envType = this.envTypesStore.getEnvType(env.envTypeId);
    const config = this.getConfiguration(env);
    const instanceType = config ? config.instanceType.split('/')[0] : env.envTypeId;
    const optionalSecurityGroupId = env.optionalSecurityGroupId;
    const enableHyperthreading = env.enableHyperthreading;

    // Parse hardware/software info to match workspace card pattern
    const hardwareInfo = config ? parseHardwareInfo(config.desc) : { cpu: null, memory: null };
    const softwareInfo = parseEnvTypeName(envType?.name);
    const configName = formatConfigName(config ? config.name : env.envTypeConfigId);

    return (
      <div className="workspace-detail-info-grid mt3">
        {/* Settings Card */}
        <div className="workspace-info-card">
          <div className="workspace-info-card-label">
            <Icon name="sliders horizontal" style={iconStyle} />
            Settings
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Hyperthreading</span>
            <Popup
              trigger={
                <span
                  className="workspace-status-badge"
                  style={{
                    background: enableHyperthreading ? '#dcfce7' : '#fee2e2',
                    color: enableHyperthreading ? '#166534' : '#991b1b',
                    fontSize: '0.65em',
                    padding: '3px 8px'
                  }}
                >
                  {enableHyperthreading ? 'ENABLED' : 'DISABLED'}
                </span>
              }
              content={`Hyperthreading is ${enableHyperthreading ? 'enabled' : 'disabled'}`}
              size="small"
            />
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Networking</span>
            <Popup
              trigger={
                <span
                  className="workspace-status-badge"
                  style={{
                    background: optionalSecurityGroupId ? '#dcfce7' : '#fee2e2',
                    color: optionalSecurityGroupId ? '#166534' : '#991b1b',
                    fontSize: '0.65em',
                    padding: '3px 8px'
                  }}
                >
                  {optionalSecurityGroupId ? 'ENABLED' : 'DISABLED'}
                </span>
              }
              content={`Cross project communication is ${optionalSecurityGroupId ? 'enabled' : 'disabled'}${optionalSecurityGroupId ? ', your workspace can connect with other workspaces in your project.' : '.'}`}
              size="small"
            />
          </div>
          {isAdmin && !this.props.userStore.researcherView && canToggleLock && (
            <div className="workspace-info-card-row" style={{ marginTop: '8px' }}>
              <span className="workspace-info-card-row-label">Termination Lock</span>
              {this.renderTerminationLock(env)}
            </div>
          )}
        </div>

        {/* Hardware Card */}
        <div className="workspace-info-card">
          <div className="workspace-info-card-label">
            <Icon name="microchip" style={iconStyle} />
            Hardware
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Instance</span>
            <span className="workspace-info-card-row-value">{instanceType}</span>
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">CPU</span>
            <span className="workspace-info-card-row-value">{hardwareInfo.cpu || 'N/A'}</span>
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Memory</span>
            <span className="workspace-info-card-row-value">{hardwareInfo.memory || 'N/A'}</span>
          </div>
        </div>

        {/* Software Card */}
        <div className="workspace-info-card">
          <div className="workspace-info-card-label">
            <Icon name="code" style={iconStyle} />
            Software
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Config</span>
            <span className="workspace-info-card-row-value-group">
              {configName.code && <span className="workspace-info-card-row-badge">{configName.code}</span>}
              {configName.truncated ? (
                <Popup
                  // No title attribute: it renders the browser's own tooltip on top of the Popup.
                  trigger={<span className="workspace-info-card-row-value">{configName.display}</span>}
                  content={configName.full}
                  size="small"
                  position="top center"
                  inverted
                />
              ) : (
                <span className="workspace-info-card-row-value">{configName.display}</span>
              )}
            </span>
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">OS</span>
            <span className="workspace-info-card-row-value">{softwareInfo.os}</span>
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Software</span>
            <span className="workspace-info-card-row-value">{softwareInfo.software}</span>
          </div>
        </div>

        {/* Storage & Studies Card */}
        <div className="workspace-info-card">
          <div className="workspace-info-card-label">
            <Icon name="database" style={iconStyle} />
            Storage & Studies
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Storage</span>
            <span className="workspace-info-card-row-value">
              {config ? (
                <StorageUsageDisplay totalGB={env.rootVolume} usedGB={env.status !== 'TERMINATED' ? env.diskUtilizationGB : null} />
              ) : (
                'Unavailable'
              )}
            </span>
          </div>
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Studies</span>
            <span className="workspace-info-card-row-value">{studyCount} mounted</span>
          </div>
          {studyCount > 0 && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontSize: '0.75em', color: '#6b7280', marginBottom: '4px' }}>Study IDs:</div>
              <div style={{ fontSize: '0.8em', color: '#374151', wordBreak: 'break-word' }}>
                {studyIds.map((id, index) => (
                  <span key={id}>
                    {id}{index < studyIds.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          {studyCount === 0 && (
            <div style={{ marginTop: '8px', fontSize: '0.8em', color: '#9ca3af', fontStyle: 'italic' }}>
              No studies mounted
            </div>
          )}
        </div>

        {/* Credit Usage Card */}
        <div className="workspace-info-card">
          <ScEnvironmentCost envId={env.id} compact cpuUtilization={env.status !== 'TERMINATED' ? env.cpuUtilization : null} cpuUtilizationUpdatedAt={env.cpuUtilizationUpdatedAt} />
        </div>

        {/* Scheduling Cards. Both are rendered whether or not anything is set, so that the feature is
            discoverable from the workspace rather than only from the Actions menu. With the feature flag
            off neither appears (Requirement 1.7). */}
        {enableWorkspaceScheduler && this.renderScheduleCard(env)}
        {enableWorkspaceScheduler && this.renderIdleStopCard(env)}
      </div>
    );
  }

  /**
   * The note that explains why a card is showing less than it could.
   *
   * Deliberately quiet and inside the card: the two cards are advisory, and the failure of one call
   * says nothing about the workspace.
   */
  renderSchedulingNote() {
    if (this.schedulingLoading) {
      return (
        <div className="workspace-info-card-note" data-testid="scheduling-card-loading">
          Loading the latest details…
        </div>
      );
    }

    if (this.schedulingFailed) {
      return (
        <div className="workspace-info-card-note" data-testid="scheduling-card-degraded">
          Live details are unavailable. Showing the last known values.
        </div>
      );
    }

    return null;
  }

  /**
   * The Schedule card: what timetable is in force, the hours it resolves to, and what the scheduler has
   * actually done.
   *
   * Prefers the response, which is authoritative, and falls back to the tag mirror so the card still
   * says something when the call failed. The resolved hours need the groups table for a preset, so the
   * window lines are shown for a preset only once the response has arrived; a custom timetable resolves
   * from the tag alone and is shown either way.
   *
   * No next scheduled start or stop. Predicting one faithfully would mean reproducing the scheduler's
   * window union, its DST-aware wall-clock arithmetic and its lookback, and a wrong prediction is worse
   * than none. The last start and stop are facts the scheduler itself recorded.
   */
  renderScheduleCard(env) {
    const info = this.schedulingInfo;
    const groups = _.isArray(_.get(info, 'schedule.groups')) ? _.get(info, 'schedule.groups') : [];
    const groupNames = groups.map(group => _.get(group, 'name')).filter(name => typeof name === 'string');
    const scheduleValue = info
      ? normaliseScheduleValue(_.get(info, 'schedule.value'))
      : normaliseScheduleValue(env.schedule);
    const model = parseScheduleValue(scheduleValue, groupNames);

    const resolvable = model.mode === MODE_CUSTOM || groups.length > 0;
    const lines = resolvable ? summariseWindows(model, groups) : [];

    // A preset list is named as the tag names it; the resolved days and hours are on the lines below.
    // A custom timetable with every day off expresses no window, which is the same as having none.
    let timetable = 'Off';
    if (model.mode === MODE_GROUPS && !_.isEmpty(model.selectedGroups)) {
      timetable = model.selectedGroups.map(displayGroupName).join(', ');
    } else if (model.mode === MODE_CUSTOM) {
      timetable = lines.length > 0 ? 'Custom' : 'Off';
    }

    const timezone = String(_.get(info, 'timezone') || schedulerTimezone || '').trim();

    // The response reads the reserved tags off the instance as the card is opened; the mirror is only
    // written by the status poll, so a workspace the scheduler started minutes ago has nothing in it
    // until the next cycle. Prefer the response and fall back to the mirror, as the idle card does with
    // `projectedStopAt`.
    const lastStartAt = _.get(info, 'schedule.lastStartAt') || env.scheduleLastStart;
    const lastStopAt = _.get(info, 'schedule.lastStopAt') || env.scheduleLastStop;

    return (
      <div className="workspace-info-card" data-testid="workspace-schedule-card">
        <div className="workspace-info-card-label">
          <Icon name="calendar alternate outline" style={iconStyle} />
          Schedule
        </div>
        <div className="workspace-info-card-row">
          <span className="workspace-info-card-row-label">Timetable</span>
          <span className="workspace-info-card-row-value">{timetable}</span>
        </div>
        {lines.length > 0 && (
          <div className="workspace-info-card-lines" data-testid="workspace-schedule-windows">
            {lines.map(line => {
              const text = formatSummaryLine(line);
              return (
                <div key={text} className="workspace-info-card-line">
                  {text}
                </div>
              );
            })}
          </div>
        )}
        {timetable !== 'Off' && timezone.length > 0 && (
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Timezone</span>
            <span className="workspace-info-card-row-value">{timezone}</span>
          </div>
        )}
        {this.renderCheckpointRow('Last auto-start', lastStartAt, timezone, 'workspace-schedule-last-start')}
        {this.renderCheckpointRow('Last auto-stop', lastStopAt, timezone, 'workspace-schedule-last-stop')}
        {this.renderSchedulingNote()}
      </div>
    );
  }

  /**
   * One of the scheduler's own checkpoints: when it last started this workspace, last stopped it on its
   * timetable, or last stopped it for being idle.
   *
   * Relative, as times are elsewhere on this page, with the absolute time in the deployment timezone on
   * hover, the same way the Will stop row gives its projection. Relative alone turns "started at 9am"
   * into "3 hours ago", which is the one thing a reader checking a timetable wants to compare against
   * the hours the card states just above.
   *
   * The timestamp is the boundary of the window the scheduler acted on, not the instant it ran: a 09:00
   * start recorded against a 09:00 window is what makes the hours above verifiable, even though the
   * invocation that performed it may have been at 09:12. The reserved tags do not carry the execution
   * time at all, so there is nothing here to "correct" it to.
   *
   * Renders nothing when the checkpoint is absent, which is every workspace the scheduler has not yet
   * acted on.
   */
  renderCheckpointRow(label, timestamp, timezone, testId) {
    if (_.isEmpty(timestamp)) return null;

    const absolute = formatProjectedStopWithZone(timestamp, timezone);
    const relative = <TimeAgo date={timestamp} />;

    return (
      <div className="workspace-info-card-row">
        <span className="workspace-info-card-row-label">{label}</span>
        <span className="workspace-info-card-row-value" data-testid={testId}>
          {absolute ? (
            <Popup trigger={<span>{relative}</span>} content={absolute} size="small" position="top center" inverted />
          ) : (
            relative
          )}
        </span>
      </div>
    );
  }

  /**
   * The Stop when idle card: the timeout, whether the user may change it, and any outstanding warning.
   *
   * The stop time prefers the server's `projectedStopAt` and falls back to the same arithmetic applied
   * to the mirror, so the card still gives a time when the call failed. When neither can produce one it
   * says the workspace will stop soon rather than inventing a time.
   *
   * Times are relative, as they are elsewhere on this page, with the absolute time in the deployment
   * timezone on hover. A live countdown would be a ticking timer on a page that has none.
   */
  renderIdleStopCard(env) {
    const idleStop = _.get(this.schedulingInfo, 'idleStop');
    const fromInfo = _.isPlainObject(idleStop);

    const locked = _.get(idleStop, 'canUserSet') === false;
    // While locked the value in force is the configuration's, which the tag may not yet carry.
    const minutes = fromInfo
      ? normaliseMinutes(idleStop.minutes) || (locked ? normaliseMinutes(idleStop.lockedMinutes) : null)
      : normaliseMinutes(env.idleStopMinutes);

    const warnedAt = fromInfo ? idleStop.warnedAt : env.idleStopWarnedAt;
    const outstanding = typeof warnedAt === 'string' && warnedAt.trim().length > 0;

    // The last idle stop the scheduler performed. The response reads it off the instance as the card
    // opens and the mirror lags a poll behind, so the response is preferred and the mirror is the
    // fallback, the same precedence `projectedStopAt` and the schedule card's checkpoints use.
    //
    // Shown whenever it is set, at any status. The scheduler never clears the tag, so a workspace that
    // has since been restarted still carries it, and that it was idle-stopped earlier remains true.
    const stoppedAt = _.get(idleStop, 'stoppedAt') || env.idleStopStoppedAt;
    const stopAtIso =
      _.get(idleStop, 'projectedStopAt') || projectedStopFrom(warnedAt, minutes, schedulerIdleGraceMultiplier);
    const timezone = String(_.get(this.schedulingInfo, 'timezone') || schedulerTimezone || '');
    const stopAtAbsolute = formatProjectedStopWithZone(stopAtIso, timezone);

    return (
      <div className="workspace-info-card" data-testid="workspace-idle-stop-card">
        <div className="workspace-info-card-label">
          <Icon name="moon outline" style={iconStyle} />
          Stop when idle
        </div>
        <div className="workspace-info-card-row">
          <span className="workspace-info-card-row-label">Timeout</span>
          <span className="workspace-info-card-row-value">{minutes ? formatDuration(minutes) : 'Off'}</span>
        </div>
        {locked && (
          <div className="workspace-info-card-note" data-testid="workspace-idle-stop-locked">
            {IDLE_STOP_LOCKED_NOTICE}
          </div>
        )}
        {outstanding && (
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Judged idle</span>
            <span className="workspace-info-card-row-value">
              <TimeAgo date={warnedAt} />
            </span>
          </div>
        )}
        {outstanding && (
          <div className="workspace-info-card-row">
            <span className="workspace-info-card-row-label">Will stop</span>
            <span className="workspace-info-card-row-value" data-testid="workspace-idle-stop-projected">
              {stopAtAbsolute ? (
                <Popup
                  trigger={
                    <span>
                      <TimeAgo date={stopAtIso} />
                    </span>
                  }
                  content={stopAtAbsolute}
                  size="small"
                  position="top center"
                  inverted
                />
              ) : (
                'Soon'
              )}
            </span>
          </div>
        )}
        {this.renderCheckpointRow('Last idle stop', stoppedAt, timezone, 'workspace-idle-stop-stopped-at')}
        {this.renderSchedulingNote()}
      </div>
    );
  }

  // Legacy renderDetailTable - kept for reference but no longer used
  // Details are now rendered in renderInfoCardGrid as part of the modern card layout
  renderDetailTable(env) {
    return null;
  }

  renderButtons(env) {
    return (
      <ScEnvironmentButtons scEnvironment={env} hideConnections />
    );
  }

  // Legacy renderTitle - kept for reference but no longer used
  // Title is now rendered in renderHeaderContent as part of the modern header card
  renderTitle(env) {
    return null;
  }

  // Legacy renderStatus - kept for reference but no longer used
  // Status is now rendered inline in renderInfoCardGrid as part of the Status & Settings card

  renderTerminationLock(env) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Dimmer inverted active={this.processing}>
          <Loader inverted />
        </Dimmer>
        <Checkbox
          fitted
          checked={env.terminationLocked}
          toggle
          onClick={() => this.handleWorkspaceLockToggle(env)}
        />
        <span
          className="workspace-status-badge"
          style={{
            background: env.terminationLocked ? '#dcfce7' : '#fee2e2',
            color: env.terminationLocked ? '#166534' : '#991b1b',
            fontSize: '0.65em',
            padding: '3px 8px'
          }}
        >
          {env.terminationLocked ? 'ENABLED' : 'DISABLED'}
        </span>
      </div>
    );
  }

  async handleWorkspaceLockToggle(env) {
    const store = this.envsStore;
    runInAction(() => {
      this.processing = true;
    });
    try {
      await store.toggleScEnvironmentLock(env.id);
    } catch (error) {
      displayError(error);
    } finally {
      runInAction(() => {
        this.processing = false;
      });
    }
  }

  // Legacy renderError - kept for reference but no longer used
  // Errors are now rendered in renderAlerts as part of the modern header card
  renderError(env) {
    return null;
  }

  renderTabs(env) {
    const panes = [
      {
        menuItem: 'Historical Cost',
        render: () => (
          <Tab.Pane attached={false} key="cost" as={TabPaneWrapper}>
            <Observer>{() => <ScEnvironmentCostTable envId={env.id} />}</Observer>
          </Tab.Pane>
        ),
      },
      {
        menuItem: 'CPU Utilisation',
        render: () => (
          <Tab.Pane attached={false} key="cpu" as={TabPaneWrapper}>
            <Observer>{() => <ScEnvironmentCpuChart envId={env.id} />}</Observer>
          </Tab.Pane>
        ),
      },
      {
        menuItem: 'Technical Information',
        render: () => (
          <Tab.Pane attached={false} key="cfn-outputs" as={TabPaneWrapper}>
            <Observer>{() => this.renderCfnOutput(env)}</Observer>
          </Tab.Pane>
        ),
      },
    ];

    return (
      <div className="workspace-detail-tabs mt4">
        <Tab
          menu={{ secondary: true, pointing: true }}
          renderActiveOnly
          panes={panes}
          defaultActiveIndex="0"
        />
      </div>
    );
  }

  renderCfnOutput(env) {
    const outputs = env.outputs;
    const isEmpty = _.isEmpty(outputs);

    // Modern empty state component
    if (isEmpty) {
      return (
        <div className="workspace-modern-table-empty mt3">
          <Icon name="info circle" style={iconStyle} />
          <div className="workspace-modern-table-empty-title">No Technical Information</div>
          <div className="workspace-modern-table-empty-text">
            CloudFormation outputs will appear here once the workspace is fully provisioned.
          </div>
        </div>
      );
    }

    // Modern table with alternating row colors
    return (
      <table className="workspace-modern-table mt3">
        <thead>
          <tr>
            <th>Key</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {_.map(outputs, (item, index) => (
            <tr key={index}>
              <td className="table-key">{item.OutputKey}</td>
              <td className="table-value">
                {item.OutputValue}
                {item.Description && (
                  <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '4px', fontFamily: 'inherit' }}>
                    {item.Description}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(ScEnvironmentDetailPage, {
  instanceId: computed,
  envsStore: computed,
  envTypesStore: computed,
  userStore: computed,
  processing: observable,
  schedulingInfo: observable,
  schedulingLoading: observable,
  schedulingFailed: observable,
});

export default inject(
  'userStore',
  'envTypesStore',
  'scEnvironmentsStore',
)(withRouter(observer(ScEnvironmentDetailPage)));
